import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import { useRelatedPosts } from '../../hooks/useBlogPosts';
import { formatDate } from '../../utils/format/date';
import { LoadingSkeleton } from '../shared/LoadingSkeleton';
import type { BlogPost } from '../../types/blog';

interface RelatedPostsProps {
  currentPost: BlogPost;
}

export function RelatedPosts({ currentPost }: RelatedPostsProps) {
  const { data: relatedPosts = [], isLoading } = useRelatedPosts(currentPost, 3);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8">
        <h2 className="text-2xl font-bold mb-6">Related Articles</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="space-y-3">
              <LoadingSkeleton className="h-32 w-full rounded-lg" />
              <LoadingSkeleton className="h-6 w-full" />
              <LoadingSkeleton className="h-4 w-3/4" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (relatedPosts.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-8">
      <h2 className="text-2xl font-bold mb-6">Related Articles</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {relatedPosts.map((post) => (
          <Link
            key={post.id}
            to={`/blog/${post.slug}`}
            className="group block"
          >
            <div className="bg-gray-50 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
              <img
                src={post.featuredImage || 'https://images.unsplash.com/photo-1639322537228-f710d846310a'}
                alt={post.title}
                className="w-full h-32 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="p-4">
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                  <Calendar className="h-3 w-3" />
                  <span>{formatDate(post.publishedAt)}</span>
                  <Clock className="h-3 w-3 ml-2" />
                  <span>{post.readTime} min</span>
                </div>
                <h3 className="font-semibold text-sm line-clamp-2 group-hover:text-emerald-600 transition-colors mb-2">
                  {post.title}
                </h3>
                <p className="text-xs text-gray-600 line-clamp-2">
                  {post.excerpt}
                </p>
                <div className="flex items-center justify-between mt-3">
                  <span 
                    className="px-2 py-1 rounded-full text-xs font-medium text-white"
                    style={{ backgroundColor: post.category.color }}
                  >
                    {post.category.name}
                  </span>
                  <ArrowRight className="h-4 w-4 text-emerald-600 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}